import { useMemo, useState } from 'react'
import {
  useGlowUsers,
  useAddUser,
  useUpdateUser,
  useDeleteUser,
  useUpdatePatterns,
  glowUserErrorMessage,
} from '../../hooks/useGlowUsers'
import { Card } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { Badge } from '../../components/Badge'
import {
  ResponsiveTable,
  type Column,
} from '../../components/ui/ResponsiveTable'
import { ErrorBanner } from '../../components/ErrorBanner'
import { SubscriptionGate } from '../../components/SubscriptionGate'
import { PermissionSelect, permLabel, permTone } from './permissions'
import { UserDialog, type UserDialogValue } from './UserDialog'
import type { AllowedPattern, Glow, GlowUser, Permission } from '../../api/types'

// UsersTab is the full-CRUD user-management surface for a Private glow. It
// replaced the old full-replace AllowlistTab: each row mutates through its
// own granular endpoint (POST/PATCH/DELETE /v1/glows/{id}/users), so there's
// no local dirty/draft/Discard machinery. The owner row (source==='owner')
// is always present, locked to Admin, and not deletable (CLAUDE.md §3).
//
// Domain patterns remain a single full-replace list (PUT /patterns), edited
// in the patterns card below the table.
export function UsersTab({ glow }: { glow: Glow }) {
  const { data: users, isLoading, error } = useGlowUsers(glow.id)
  const addUser = useAddUser(glow.id)
  const updateUser = useUpdateUser(glow.id)
  const deleteUser = useDeleteUser(glow.id)

  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  if (glow.auth_mode === 'Public') {
    return (
      <Card>
        <h3 className="font-medium">User list is unused</h3>
        <p className="mt-1 text-sm text-cream/70">
          This glow is Public — anyone can visit without signin. Allowed
          users apply to Private glows; Accounts glows populate their roster
          from self-serve signups.
        </p>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (error) {
    return <ErrorBanner>{(error as Error).message}</ErrorBanner>
  }

  const rows = users ?? []

  // submitDialog routes the dialog result to the right endpoint: an add
  // (no editing uid) POSTs; an edit PATCHes by uid. Errors surface inline.
  const submitDialog = async (value: UserDialogValue) => {
    setRowError(null)
    try {
      if (dialog?.kind === 'edit') {
        await updateUser.mutateAsync({
          uid: dialog.user.uid,
          body: dialog.lockIdentity
            ? { name: value.name, mobile: value.mobile }
            : { name: value.name, mobile: value.mobile, permission: value.permission },
        })
      } else {
        await addUser.mutateAsync({
          email: value.email,
          name: value.name,
          mobile: value.mobile,
          permission: value.permission,
        })
      }
      setDialog(null)
    } catch (e) {
      setRowError(glowUserErrorMessage(e, 'Could not save the user.'))
    }
  }

  const remove = async (u: GlowUser) => {
    setRowError(null)
    try {
      await deleteUser.mutateAsync(u.uid)
    } catch (e) {
      setRowError(glowUserErrorMessage(e, 'Could not remove the user.'))
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-medium text-cream">Allowed users</h3>
            <p className="mt-0.5 text-xs text-muted">
              Users who can sign in to this Private glow. Your own account is
              always an Admin and cannot be removed; you can still edit your
              name and mobile. Pattern-matched users appear here too — editing
              one pins it to this glow.
            </p>
          </div>
        </div>

        <div className="mt-3 overflow-hidden rounded-md border border-line">
          <ResponsiveTable
            columns={userColumns}
            rows={rows}
            rowKey={(u) => u.uid}
            actions={(u) => {
              const owner = u.source === 'owner'
              return (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setRowError(null)
                      setDialog({ kind: 'edit', user: u, lockIdentity: owner })
                    }}
                  >
                    Edit
                  </Button>
                  {!owner && (
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => remove(u)}
                      disabled={deleteUser.isPending}
                    >
                      Remove
                    </Button>
                  )}
                </>
              )
            }}
            emptyState={
              <p className="px-3 py-3 text-sm text-muted">No users listed.</p>
            }
          />
        </div>

        <SubscriptionGate flag="can_add_emails">
          {({ locked }) => (
            <div className="mt-3">
              <Button
                type="button"
                variant="secondary"
                disabled={locked}
                onClick={() => {
                  setRowError(null)
                  setDialog({ kind: 'add' })
                }}
              >
                Add user
              </Button>
            </div>
          )}
        </SubscriptionGate>
      </Card>

      <PatternsCard glow={glow} />

      {/* Tab-level banner is for the row-action (Remove) errors, which happen
          with no dialog open. A dialog-submit error is passed INTO the dialog
          (below) so it renders inside the modal, not behind its dim overlay. */}
      {!dialog && rowError && <ErrorBanner>{rowError}</ErrorBanner>}

      {dialog && (
        <UserDialog
          mode={dialog.kind}
          lockIdentity={dialog.kind === 'edit' ? dialog.lockIdentity : false}
          error={rowError}
          glowRegion={glow.region}
          initial={
            dialog.kind === 'edit'
              ? {
                  name: dialog.user.name,
                  email: dialog.user.email,
                  mobile: dialog.user.mobile,
                  permission: dialog.user.permission,
                }
              : { permission: 'user' }
          }
          onCancel={() => {
            setRowError(null)
            setDialog(null)
          }}
          onSave={submitDialog}
        />
      )}
    </div>
  )
}

// userColumns drives the allowed-users table (and its stacked-card form
// below sm). Pure — the cells reference only the module-level permTone /
// permLabel / sourceTone / sourceLabel helpers, so this lives at module
// scope; the per-row Edit/Remove cluster is wired via the `actions` slot in
// UsersTab (it needs setDialog / remove / deleteUser from component scope).
const dash = <span className="text-muted">—</span>
const userColumns: Column<GlowUser>[] = [
  {
    header: 'Name',
    primary: true,
    cell: (u) =>
      u.name ? (
        <span className="font-medium text-cream">{u.name}</span>
      ) : (
        dash
      ),
  },
  { header: 'Email', cell: (u) => u.email || dash },
  {
    header: 'Mobile',
    // A non-empty mobile is the SMS-login identifier — flag it with a visible
    // "SMS" badge (text, not colour-only). Empty cells keep rendering dash.
    cell: (u) =>
      u.mobile ? (
        <span className="inline-flex items-center gap-1.5">
          <span>{u.mobile}</span>
          <Badge tone="green">SMS</Badge>
        </span>
      ) : (
        dash
      ),
  },
  {
    header: 'Permission',
    cell: (u) => <Badge tone={permTone(u.permission)}>{permLabel(u.permission)}</Badge>,
  },
  {
    header: 'Source',
    cell: (u) => <Badge tone={sourceTone(u.source)}>{sourceLabel(u.source)}</Badge>,
  },
]

// PatternsCard edits the glow's domain-pattern allowlist. Patterns are still
// a single array on the glow, so this stays a full-replace card behind the
// can_add_patterns gate — each change immediately PUTs the whole list.
function PatternsCard({ glow }: { glow: Glow }) {
  const updatePatterns = useUpdatePatterns(glow.id)
  const patterns = useMemo(() => glow.allowed_patterns ?? [], [glow.allowed_patterns])
  const [draft, setDraft] = useState('')
  const [err, setErr] = useState<string | null>(null)

  const save = async (next: AllowedPattern[]) => {
    setErr(null)
    try {
      await updatePatterns.mutateAsync(next)
    } catch (e) {
      setErr(glowUserErrorMessage(e, 'Could not save patterns.'))
    }
  }

  const add = () => {
    const v = draft.trim().toLowerCase()
    if (!v) return
    if (!/^@[^\s@]+\.[^\s@]+$/.test(v)) {
      setErr('Patterns must look like @example.com.')
      return
    }
    if (patterns.some((p) => p.pattern.toLowerCase() === v)) {
      setErr('That pattern is already listed.')
      return
    }
    setDraft('')
    void save([...patterns, { pattern: v, permission: 'user' }])
  }

  const setPermission = (idx: number, permission: Permission) => {
    void save(patterns.map((p, i) => (i === idx ? { ...p, permission } : p)))
  }

  const remove = (idx: number) => {
    void save(patterns.filter((_, i) => i !== idx))
  }

  return (
    <Card>
      <h3 className="text-sm font-medium text-cream">Allowed domain patterns</h3>
      <p className="mt-0.5 text-xs text-muted">
        Patterns like <code className="rounded bg-surface-2 px-1">@example.com</code> admit any
        address at that domain. Each pattern grants the chosen permission.
      </p>
      <ul className="mt-3 divide-y divide-white/5 rounded-md border border-line">
        {patterns.length === 0 && (
          <li className="px-3 py-2 text-sm text-muted">No patterns listed.</li>
        )}
        {patterns.map((p, idx) => (
          <li
            key={p.pattern}
            className="flex flex-col gap-2 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
          >
            <span>{p.pattern}</span>
            <span className="flex items-center gap-3">
              <PermissionSelect
                value={p.permission}
                onChange={(perm) => setPermission(idx, perm)}
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={updatePatterns.isPending}
                className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
              >
                Remove
              </button>
            </span>
          </li>
        ))}
      </ul>
      <SubscriptionGate flag="can_add_patterns">
        {({ locked }) => (
          <div className="mt-3 flex flex-wrap gap-2 sm:flex-nowrap">
            <Input
              type="text"
              placeholder="@example.com"
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              disabled={locked}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  ev.preventDefault()
                  if (!locked) add()
                }
              }}
            />
            <Button type="button" onClick={add} disabled={locked || updatePatterns.isPending}>
              Add
            </Button>
          </div>
        )}
      </SubscriptionGate>
      {err && <ErrorBanner className="mt-3">{err}</ErrorBanner>}
    </Card>
  )
}

type DialogState =
  | { kind: 'add' }
  | { kind: 'edit'; user: GlowUser; lockIdentity: boolean }

function sourceLabel(source: GlowUser['source']): string {
  switch (source) {
    case 'owner':
      return 'Owner'
    case 'manual':
      return 'Manual'
    case 'pattern':
      return 'Pattern'
  }
}

function sourceTone(source: GlowUser['source']): 'blue' | 'slate' | 'green' {
  switch (source) {
    case 'owner':
      return 'blue'
    case 'pattern':
      return 'green'
    case 'manual':
      return 'slate'
  }
}
